import type * as Matter from "matter-js";
import { requiredChild } from "./Dom";

export function createSmashKeyboard(
	matter: typeof Matter,
	engine: Matter.Engine,
	power: number,
) {
	const { Bodies, Body, Constraint, World } = matter;
	const impulse = Math.sqrt(power);
	const logo = requiredChild<SVGSVGElement>(document, ".homepage > .hero");
	const cable = requiredChild<SVGPathElement>(logo, "#L");
	const plate = requiredChild<SVGRectElement>(logo, "#B").getBBox();
	const matrix = logo.getScreenCTM();
	if (!matrix) {
		throw new Error("Missing keyboard coordinate transform");
	}
	const inverse = matrix.inverse();
	const scale = Math.hypot(matrix.a, matrix.b);
	const centre = new DOMPoint(
		plate.x + plate.width / 2,
		plate.y + plate.height / 2,
	);
	const screenCentre = centre.matrixTransform(matrix);
	const length = cable.getTotalLength();
	const pointAt = (distance: number): DOMPoint =>
		cable.getPointAtLength(distance).matrixTransform(matrix);

	// Discard the cable above the viewport, keeping its visible crossing pinned to the screen edge.
	let start = 0;
	while (start < length && pointAt(start).y < 0) {
		start = Math.min(length, start + length / 256);
	}
	let before = Math.max(0, start - length / 256);
	for (let i = 0; i < 12; i++) {
		const middle = (before + start) / 2;
		if (pointAt(middle).y < 0) {
			before = middle;
		} else {
			start = middle;
		}
	}
	const anchor = pointAt(start);
	anchor.y = 0;
	const plug = pointAt(length);
	const attachment = { x: plug.x - screenCentre.x, y: plug.y - screenCentre.y };
	const keyboard = Bodies.rectangle(
		screenCentre.x,
		screenCentre.y,
		plate.width * scale,
		plate.height * scale,
		{ collisionFilter: { mask: 0 }, frictionAir: 0.008 },
	);
	const pin = Bodies.circle(anchor.x, anchor.y, 1, {
		isStatic: true,
		collisionFilter: { mask: 0 },
	});
	const links = [pin];
	const segments = 16;
	World.add(engine.world, [keyboard, pin]);
	for (let i = 1; i <= segments; i++) {
		const point = pointAt(start + ((length - start) * i) / segments);
		const end = i === segments;
		const body = end
			? keyboard
			: Bodies.circle(point.x, point.y, 2, {
					collisionFilter: { mask: 0 },
					frictionAir: 0.025,
					inertia: Infinity,
				});
		if (!end) {
			Body.setMass(body, (keyboard.mass * 0.2) / segments);
			World.add(engine.world, body);
		}
		const previous = links.at(-1)!;
		World.add(
			engine.world,
			Constraint.create({
				bodyA: previous,
				bodyB: body,
				pointB: end ? { ...attachment } : { x: 0, y: 0 },
				length: Math.hypot(
					point.x - previous.position.x,
					point.y - previous.position.y,
				),
				stiffness: 1,
				damping: 0.08,
			}),
		);
		if (!end) {
			links.push(body);
		}
	}
	// The blast lifts the free cable links along with the keyboard, but not the pinned end.
	for (const body of [keyboard, ...links.slice(1)]) {
		Body.setVelocity(body, { x: 3 * impulse, y: -7 * impulse });
	}
	Body.setAngularVelocity(keyboard, 0.015 * impulse);

	const hKey = requiredChild<SVGGElement>(logo, "#H");
	const hBounds = hKey.getBBox();
	const hCentre = new DOMPoint(
		hBounds.x + hBounds.width / 2,
		hBounds.y + hBounds.height / 2,
	);
	const flyingKeys = (["W", "O"] as const).map((id) => {
		const node = requiredChild<SVGGElement>(logo, `#${id}`);
		const bounds = node.getBBox();
		const keyCentre = new DOMPoint(
			bounds.x + bounds.width / 2,
			bounds.y + bounds.height / 2,
		);
		const position = keyCentre.matrixTransform(matrix);
		const body = Bodies.rectangle(
			position.x,
			position.y,
			bounds.width * scale,
			bounds.height * scale,
			{ collisionFilter: { mask: 0 }, frictionAir: 0.008 },
		);
		Body.setVelocity(body, {
			x:
				(id === "W" ? -1 : 1) *
				Math.max(1.5, document.documentElement.clientWidth * 0.004) *
				impulse,
			y: -12 * impulse,
		});
		Body.setAngularVelocity(body, (id === "W" ? -0.06 : 0.06) * impulse);
		World.add(engine.world, body);
		return {
			node,
			centre: keyCentre,
			body,
			radius: (Math.hypot(bounds.width, bounds.height) * scale) / 2,
		};
	});

	// Leave an empty SVG in flow; the original artwork becomes a clipped, viewport-fixed scene.
	const placeholder = document.createElementNS(logo.namespaceURI, "svg");
	placeholder.setAttribute("class", logo.getAttribute("class") ?? "");
	placeholder.setAttribute("viewBox", logo.getAttribute("viewBox") ?? "");
	placeholder.setAttribute("aria-hidden", "true");
	logo.before(placeholder);
	const scene = document.createElementNS(logo.namespaceURI, "g");
	const board = document.createElementNS(logo.namespaceURI, "g");
	for (const child of [...logo.children]) {
		if (child instanceof SVGGElement) {
			if (flyingKeys.some(({ node }) => node === child)) {
				continue;
			}
			if (child.querySelector('use[href="#L"]')) {
				scene.append(child);
			} else {
				board.append(child);
			}
		}
	}
	scene.setAttribute(
		"transform",
		`matrix(${matrix.a} ${matrix.b} ${matrix.c} ${matrix.d} ${matrix.e} ${matrix.f})`,
	);
	scene.append(board, ...flyingKeys.map(({ node }) => node));
	logo.append(scene);
	logo.classList.add("smashing");
	logo.setAttribute("preserveAspectRatio", "none");
	const viewport = document.documentElement;
	const anchorFraction = anchor.x / viewport.clientWidth;
	let viewportWidth = 0;
	let viewportHeight = 0;
	let hasPrevious = false;
	const previousBoard = { x: 0, y: 0, angle: 0 };
	const previousLinks = links.map(() => ({ x: 0, y: 0 }));
	const previousKeys: { x: number; y: number; angle: number }[] = [];
	const rememberPrevious = () => {
		hasPrevious = true;
		previousBoard.x = keyboard.position.x;
		previousBoard.y = keyboard.position.y;
		previousBoard.angle = keyboard.angle;
		for (let i = 0; i < links.length; i++) {
			previousLinks[i]!.x = links[i]!.position.x;
			previousLinks[i]!.y = links[i]!.position.y;
		}
		for (let i = 0; i < flyingKeys.length; i++) {
			const { body } = flyingKeys[i]!;
			const previous = (previousKeys[i] ??= { x: 0, y: 0, angle: 0 });
			previous.x = body.position.x;
			previous.y = body.position.y;
			previous.angle = body.angle;
		}
	};

	const draw = (fraction = 1) => {
		const alpha = hasPrevious ? fraction : 1;
		const between = (startValue: number, endValue: number) =>
			startValue + (endValue - startValue) * alpha;

		if (
			viewportWidth !== viewport.clientWidth ||
			viewportHeight !== viewport.clientHeight
		) {
			// Mobile innerWidth can grow with the exploding text's overflow, even without a resize.
			viewportWidth = viewport.clientWidth;
			viewportHeight = viewport.clientHeight;
			logo.style.width = `${viewportWidth}px`;
			logo.style.height = `${viewportHeight}px`;
			logo.setAttribute("viewBox", `0 0 ${viewportWidth} ${viewportHeight}`);
			Body.setPosition(pin, { x: anchorFraction * viewportWidth, y: 0 });
		}
		const boardX = between(previousBoard.x, keyboard.position.x);
		const boardY = between(previousBoard.y, keyboard.position.y);
		const boardAngle = between(previousBoard.angle, keyboard.angle);
		const position = new DOMPoint(boardX, boardY).matrixTransform(inverse);
		board.setAttribute(
			"transform",
			`translate(${position.x} ${position.y}) rotate(${(boardAngle * 180) / Math.PI}) translate(${-centre.x} ${-centre.y})`,
		);
		for (let i = 0; i < flyingKeys.length; i++) {
			const { node, centre: keyCentre, body, radius } = flyingKeys[i]!;
			const previous = previousKeys[i] ?? {
				x: body.position.x,
				y: body.position.y,
				angle: body.angle,
			};
			if (body.position.y - radius > viewportHeight) {
				node.style.visibility = "hidden";
				continue;
			}
			const keyPosition = new DOMPoint(
				between(previous.x, body.position.x),
				between(previous.y, body.position.y),
			).matrixTransform(inverse);
			node.setAttribute(
				"transform",
				`translate(${keyPosition.x} ${keyPosition.y}) rotate(${(between(previous.angle, body.angle) * 180) / Math.PI}) translate(${-keyCentre.x} ${-keyCentre.y})`,
			);
		}
		const cos = Math.cos(boardAngle);
		const sin = Math.sin(boardAngle);
		const points = links.map(({ position: p }, i) =>
			new DOMPoint(
				between(previousLinks[i]!.x, p.x),
				between(previousLinks[i]!.y, p.y),
			).matrixTransform(inverse),
		);
		points.push(
			new DOMPoint(
				boardX + attachment.x * cos - attachment.y * sin,
				boardY + attachment.x * sin + attachment.y * cos,
			).matrixTransform(inverse),
		);
		const first = points[0]!;
		let path = `M${first.x} ${first.y}`;
		for (let i = 1; i < points.length - 1; i++) {
			const point = points[i]!;
			const next = points[i + 1]!;
			path += `Q${point.x} ${point.y} ${(point.x + next.x) / 2} ${(point.y + next.y) / 2}`;
		}
		const last = points.at(-1)!;
		path += `L${last.x} ${last.y}`;
		cable.setAttribute("d", path);
	};

	let releasedH = false;
	let descended = false;
	const releaseH = () => {
		releasedH = true;
		const offset = new DOMPoint(
			hCentre.x - centre.x,
			hCentre.y - centre.y,
			0,
			0,
		).matrixTransform(matrix);
		const cos = Math.cos(keyboard.angle);
		const sin = Math.sin(keyboard.angle);
		const arm = {
			x: offset.x * cos - offset.y * sin,
			y: offset.x * sin + offset.y * cos,
		};
		const body = Bodies.rectangle(
			keyboard.position.x + arm.x,
			keyboard.position.y + arm.y,
			hBounds.width * scale,
			hBounds.height * scale,
			{ collisionFilter: { mask: 0 }, frictionAir: 0.008 },
		);
		Body.setAngle(body, keyboard.angle);
		// A point on the rotating board carries tangential velocity as well as translation.
		const velocity = Body.getVelocity(keyboard);
		const angularVelocity = Body.getAngularVelocity(keyboard);
		Body.setVelocity(body, {
			x: velocity.x - angularVelocity * arm.y,
			y: velocity.y + angularVelocity * arm.x,
		});
		Body.setAngularVelocity(body, angularVelocity);
		World.add(engine.world, body);
		scene.append(hKey);
		flyingKeys.push({
			node: hKey,
			centre: hCentre,
			body,
			radius: (Math.hypot(hBounds.width, hBounds.height) * scale) / 2,
		});
		draw();
	};

	return {
		draw,
		rememberPrevious,
		releaseHAtTrough: () => {
			if (releasedH) {
				return;
			}
			// Ignore the initial upward launch; release on the first downward-to-upward reversal.
			if (keyboard.position.y > screenCentre.y && keyboard.velocity.y > 0) {
				descended = true;
			} else if (descended && keyboard.velocity.y <= 0) {
				releaseH();
			}
		},
	};
}
